import React, { useState, useEffect } from 'react';

// Validation libraries
import { useFormik } from 'formik';
import * as yup from 'yup';

// Hooks
import { NavLink, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// Action
import { createUser, createUserNewFlow } from '~/redux/actions/auth-actions';
import { failure } from '~/redux/actions/snackbar-actions';

// Utils
import { TERMS_URL, PHONE_NO_REGEX } from '~/utils/constants';
import { validateForm, validateFormAsync, realTimeValidation } from '~/utils/formik-validation';
import { MESSAGES } from '~/utils/message';
import OlcButton from '~/components/General/OlcButton';

// MUI Components
import {
  Box,
  Input,
  InputLabel,
  Typography,
  Checkbox,
  FormGroup,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
// Icons
import { OnBoardCIcon, UncheckedCIcon, CheckedCIcon } from '~/assets/images';

// Styles
import '../styles.scss';

const AccountCompletion = ({ onBoardedUser, onboardingToken }) => {
  const [termsCheck, setTermsCheck] = useState(false);
  const [newsLetterCheck, setNewsLetterCheck] = useState(true);
  const [loading, setLoading] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState(null);
  const dispatch = useDispatch();

  const formatPhoneNumber = (value) => {
    const digits = String(value || '')
      .replace(/\D/g, '')
      .slice(0, 10);
    const len = digits.length;
    if (len === 0) return '';
    if (len < 4) return `(${digits}`;
    if (len < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handlePhoneChange = (event) => {
    const formatted = formatPhoneNumber(event.target.value);
    formik.setFieldValue('phoneNo', formatted);
  };

  const validationSchema = yup.object().shape({
    company: yup.string().nullable(),
    phoneNo: yup
      .string()
      .matches(PHONE_NO_REGEX, MESSAGES.AUTH.INVALID_PHONE)
      .max(20, MESSAGES.SIGN_UP.PHONE_MAX_LENGTH)
      .required(MESSAGES.SIGN_UP.PHONE_REQUIRED),
  });

  const formik = useFormik({
    initialValues: {
      company: '',
      phoneNo: '',
      email: onBoardedUser?.email || searchParams.get('email') || '',
    },
    validationSchema: validationSchema,
    context: { code },
  });

  useEffect(() => {
    if (searchParams.size > 0) {
      if (searchParams.get('token') && searchParams.get('invited')) {
        let paramsToken = searchParams.get('token');
        let invitedEmail = searchParams.get('invited');
        formik.setFieldValue(
          'email',
          decodeURIComponent((invitedEmail + '').replace(/\%20/g, '+'))
        );
        setCode(paramsToken);
      }
    }
  }, []);

  const sendEmailToFirstPromoter = (email) => {
    if (window.fpr) {
      window.fpr('referral', { email });
    }
  };

  const register = async (event) => {
    try {
      event.preventDefault();
      const isValid = await validateFormAsync(formik);
      if (!isValid) {
        if (!termsCheck) {
          setTermsError(true);
        }
        return;
      }
      if (!termsCheck) {
        setTermsError(true);
        return;
      }
      const payload = {
        phoneNo: formik.values.phoneNo.trimStart(),
        email: onBoardedUser?.email || formik.values.email,
        companyName: formik.values.company.trim(),
        firstName: onBoardedUser?.firstName,
        lastName: onBoardedUser?.lastName,
        password: onBoardedUser?.password,
        isNewsletterSubscribed: newsLetterCheck,
        secretKey: code,
        onboardingToken,
      };
      setLoading(true);
      const joinTeam = code;
      const request = await dispatch(createUserNewFlow(payload, joinTeam));
      setLoading(false);
      if (request.message === 'success') {
        sendEmailToFirstPromoter(payload.email);
        return;
      } else {
        dispatch(
          failure(
            request?.response?.data?.data?.errors[0]?.msg ||
              request?.response?.data?.message ||
              MESSAGES.GENERAL_ERROR
          )
        );
        if (request?.response?.data?.data?.errors?.length) {
          let serverSideError = request?.response?.data?.data?.errors;
          serverSideError.forEach((error) => {
            formik.errors[error.path] = error.msg;
            formik.touched[error.path] = true;
          });
        }
      }
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    formik.touched.company = false;
    formik.touched.phoneNo = false;
    formik.touched.email = false;
  }, []);

  return (
    <Box className="newCreateAccountWrapper">
      <Box className="topIconWrapper">
        <img src={OnBoardCIcon} alt="OpenLetter" />
      </Box>
      <Box className="createAccountFormWrapper">
        <Box className="createAccountFormHeader">
          <Typography variant="h4">{MESSAGES.SIGN_UP.NEW_TEXT.COMPLETE_ACCOUNT_SETUP}</Typography>
          <Typography>{MESSAGES.SIGN_UP.NEW_TEXT.COMPLETE_FORM_TEXT}</Typography>
        </Box>
        <form onSubmit={register}>
          <Box className="newInputWrapper">
            <InputLabel className="formLabel">
              {MESSAGES.SIGN_UP.NEW_TEXT.INPUT_LABEL_EMAIL_NEW}
              <span style={{ position: 'absolute', top: '-3px' }}>*</span>
            </InputLabel>
            <Input
              type="text"
              readOnly
              placeholder={MESSAGES.SIGN_UP.NEW_TEXT.EMAIL_PLACEHOLDER_NEW}
              autoComplete="off"
              name="email"
              disabled
              className="readOnlyFormInput newFormInput"
              onBlur={realTimeValidation ? formik.handleBlur : undefined}
              value={formik.values.email}
            />
          </Box>
          {!code && (
            <Box className="newInputWrapper">
              <InputLabel className="formLabel">
                {MESSAGES.SIGN_UP.NEW_TEXT.INPUT_LABEL_COMPANY_NEW}
              </InputLabel>
              <Input
                type="text"
                placeholder={MESSAGES.SIGN_UP.NEW_TEXT.COMPANY_PLACEHOLDER_NEW}
                autoComplete="off"
                name="company"
                onChange={formik.handleChange}
                className={`newFormInput ${formik.errors.company && formik.touched.company ? 'invalid' : ''}`}
                onBlur={realTimeValidation ? formik.handleBlur : undefined}
                value={formik.values.company}
              />
              {formik.touched.company && formik.errors.company && (
                <Typography className="errorMessageNew">
                  <sup>*</sup>
                  {formik.errors.company}
                </Typography>
              )}
            </Box>
          )}
          <Box className="newInputWrapper">
            <InputLabel className="formLabel">
              {MESSAGES.SIGN_UP.NEW_TEXT.INPUT_LABEL_PHONE_NEW || 'Phone Number'}
              <span style={{ position: 'absolute', top: '-3px' }}>*</span>
            </InputLabel>
            <Input
              type="tel"
              placeholder={MESSAGES.SIGN_UP.NEW_TEXT.PHONE_NO_PLACEHOLDER_NEW}
              autoComplete="off"
              name="phoneNo"
              onChange={handlePhoneChange}
              className={`newFormInput ${formik.errors.phoneNo && formik.touched.phoneNo ? 'invalid' : ''}`}
              onBlur={realTimeValidation ? formik.handleBlur : undefined}
              value={formik.values.phoneNo}
            />
            {formik.touched.phoneNo && formik.errors.phoneNo && (
              <Typography className="errorMessageNew">
                <sup>*</sup>
                {formik.errors.phoneNo}
              </Typography>
            )}
          </Box>

          <FormGroup>
            <FormControlLabel
              className="checkBoxLabel"
              control={
                <Checkbox
                  className={termsCheck ? `customCheckbox checked` : `customCheckbox`}
                  onClick={() => setTermsCheck(!termsCheck)}
                  icon={<img src={UncheckedCIcon} alt="Not Checked" />}
                  checkedIcon={<img src={CheckedCIcon} alt="Checked" />}
                  checked={termsCheck}
                />
              }
              label={
                <Typography
                  className={!termsCheck && termsError ? `termsCheck errorCheck` : `termsCheck`}
                >
                  {MESSAGES.SIGN_UP.NEW_TEXT.AGREE_TEXT_NEW_PART1}
                  <NavLink to={TERMS_URL} target="_blank">
                    {MESSAGES.SIGN_UP.NEW_TEXT.TERMS}
                  </NavLink>
                  {MESSAGES.SIGN_UP.NEW_TEXT.AGREE_TEXT_NEW_PART2}
                  <NavLink to={TERMS_URL} target="_blank">
                    {MESSAGES.SIGN_UP.NEW_TEXT.PRIVACY_POLICY}
                  </NavLink>
                </Typography>
              }
            />
          </FormGroup>
          <FormGroup>
            <FormControlLabel
              className="checkBoxLabel"
              control={
                <Checkbox
                  className={newsLetterCheck ? `customCheckbox checked` : `customCheckbox`}
                  onClick={() => setNewsLetterCheck(!newsLetterCheck)}
                  icon={<img src={UncheckedCIcon} alt="Not Checked" />}
                  checkedIcon={<img src={CheckedCIcon} alt="Checked" />}
                  checked={newsLetterCheck}
                />
              }
              label={
                <Typography className="termsCheck">
                  {MESSAGES.SIGN_UP.NEW_TEXT.EMAILS_AND_PRODUCT_UPDATES}
                </Typography>
              }
            />
          </FormGroup>

          <OlcButton
            variant="primary"
            type="submit"
            className="submitButton"
          >
            {loading ? (
              <CircularProgress
                sx={{
                  color: 'white',
                  width: '25px !important',
                  height: '25px !important',
                }}
              />
            ) : (
              'Create Account'
            )}
          </OlcButton>
        </form>
      </Box>
    </Box>
  );
};

export default AccountCompletion;
